/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/sendInvoice.scss';

import {AwesomeIcon, ChangeFunction, CloseButton, ErrorType, Loader, WithBackground} from '@dvrd/dvr-controls';

import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import EmailEditor, {EditorRef} from 'react-email-editor';

import {getMergeTags} from '../../../util/unlayerUtil';

import {AppContext} from '../../misc/appContext';

import {BolmateButton, BolmateInput} from '../../controls';

import Invoice from '../../../models/invoiceModel';

import {UnlayerDesign} from '../../../util/interfaces';
import simpleInvoiceTemplate from '../../../../res/static/emailTemplates/invoice-simple.json';

interface Props {
    onClose: MouseEventHandler;
    onSubmit: (data: {design: UnlayerDesign; html: string}) => void;
    onChangeSubject: ChangeFunction;
    active: boolean;
    subject: string;
    loading: boolean;
    invoice: Invoice | null;
    error: ErrorType;
}

export default function SendInvoice(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {active, onChangeSubject, subject, onSubmit, onClose, loading, invoice, error} = props;
    const editorRef = useRef<EditorRef>(null);
    const [loadingEditor, setLoadingEditor] = useState(true);

    function onEditorLoaded() {
        setLoadingEditor(false);
    }

    function _onSubmit() {
        if (!editorRef.current) return;
        editorRef.current?.editor?.exportHtml((data: {design: UnlayerDesign; html: string}) => {
            onSubmit(data);
        });
    }

    function setDesign() {
        if (!editorRef.current) return;
        let design: UnlayerDesign | null = getDesignFromAccount();
        if (!design) design = simpleInvoiceTemplate as unknown as UnlayerDesign;
        editorRef.current?.editor?.loadDesign(design!);
        editorRef.current?.editor?.setMergeTags(getMergeTags(context.accountContext.account));
    }

    function getDesignFromAccount(): UnlayerDesign | null {
        const manualMailJson = context.accountContext.account?.settings?.manualMailJson;
        if (!manualMailJson || !Object.keys(manualMailJson).length) return null;
        return manualMailJson;
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <Loader active={loading} />
                {renderInvoiceDetails()}
                <div className='email-container'>
                    <BolmateInput
                        onChange={onChangeSubject}
                        value={subject}
                        label={t('invoice.send.subjectRequired', 'Onderwerp *')}
                        inputProps={{placeholder: t('invoice.send.subjectPlaceholder', 'Onderwerp van de e-mail')}}
                    />
                    <div>
                        <EmailEditor
                            ref={editorRef}
                            onReady={onEditorLoaded}
                            projectId={43580}
                            minHeight={400}
                            options={{
                                features: {
                                    smartMergeTags: false
                                }
                            }}
                        />
                    </div>
                </div>
                <label className='attachment-label'>
                    <AwesomeIcon name='paperclip' />{' '}
                    <span className='bold'>{t('invoice.send.attachment', 'Bijlage:')}</span> {invoice?.orderId}.pdf
                </label>
                <label className='error'>{error}</label>
            </div>
        );
    }

    function renderInvoiceDetails() {
        return (
            <div className='invoice-details'>
                <div className='field'>
                    <label className='key'>{t('invoice.send.orderNumber', 'Ordernummer:')}</label>
                    <label>{invoice?.orderId}</label>
                </div>
                <div className='field'>
                    <label className='key'>{t('invoice.send.invoiceNumber', 'Factuurnummer:')}</label>
                    <label>{invoice?.invoiceNum}</label>
                </div>
                <div className='field'>
                    <label className='key'>{t('invoice.send.invoiceDate', 'Factuurdatum:')}</label>
                    <label>{invoice?.invoiceDate.format('DD-MM-YYYY')}</label>
                </div>
                <div className='field'>
                    <label className='key'>{t('invoice.send.customer', 'Klant:')}</label>
                    <label>{invoice?.invoiceName}</label>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (!loadingEditor && active) setDesign();
        if (!active) setLoadingEditor(true);
    }, [loadingEditor, active]);

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='send-invoice-popup default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('invoice.send.title', 'Factuur verzenden')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container'>
                    <label className='disclaimer'>
                        {t(
                            'invoice.send.disclaimer',
                            'Voor deze e-mail worden niet de instellingen van de E-mails pagina gebruikt.'
                        )}
                    </label>
                    <div className='buttons-container'>
                        <BolmateButton
                            onClick={onClose}
                            label={t('common.cancel', 'Annuleren')}
                            disabled={loading}
                            secondary
                        />
                        <BolmateButton
                            onClick={_onSubmit}
                            label={t('invoice.send.send', 'Verzenden')}
                            disabled={loading || loadingEditor}
                        />
                    </div>
                </div>
            </div>
        </WithBackground>
    );
}
